import React, { useContext, useEffect, useMemo, useState } from "react";
import GeniePage from "../../../components/special/genie-page/GeniePage";
import GenieCard from "../../../components/special/genie-card/GenieCard";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
} from "@mui/material";
import GenieText from "../../../components/core/genie-text/GenieText";
import { ArrowBack } from "@mui/icons-material";
import GenieStickyHeader from "../../../components/core/genie-sticky-header/GenieStickyHeader";
import GenieThumbList from "../../../components/core/genie-thumb-list/GenieThumbList";
import GenieThumbUpload from "../../../components/core/genie-thumb-upload/GenieThumbUpload";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import NumberFormat from "react-number-format";
import { SecurityContext } from "../../Base";
import { apiUrl, getApiHeaders } from "../../../utils/apiUrl";
import { apiToForm, formToApi } from "./utils/translation";
import { Product, ProductMedia } from "../../../types/api/Product";
import { Nullable } from "../../../utils/Nullable";
import useProduct from "../../../queries/routes/useProduct";

const DEBUG = false;

const defaultValues: {
  media: any[];
  name: string;
  description: string;
  order_unit: string;
  retail_units: number | string;
  state: string;
  price: string;
  msrp: string;
  stock_keeping_unit: string;
  universal_product_code: string;
  minimum_order_quantity: number;
  inventory_count: string;
  weight_unit: string;
  weight: Nullable<number | string>;
  length: Nullable<number | string>;
  width: Nullable<number | string>;
  height: Nullable<number | string>;
  dimension_unit: string;
} = {
  name: "",
  description: "",
  order_unit: "",
  retail_units: "",
  minimum_order_quantity: 1,
  state: "",
  price: "",
  msrp: "",
  stock_keeping_unit: "",
  universal_product_code: "",
  inventory_count: "",
  weight_unit: "",
  weight: "",
  length: "",
  width: "",
  height: "",
  dimension_unit: "",
  media: [],
};
export type ProductForm = typeof defaultValues;

function Products() {
  const navigate = useNavigate();
  const match = useMatch("/app/:org_id/products/:id");
  let { org_id } = useParams();
  const securityContext = useContext(SecurityContext);

  const [formValues, setFormValues] = useState(defaultValues);

  const [updateNotification, setUpdateNotification] = useState(false);

  const translateProductToTemplate = (apiProduct: Product | undefined) => {
    if (apiProduct) {
      setFormValues(apiToForm(apiProduct));
    }
  };

  const { data: product } = useProduct(match?.params.id || "");

  useMemo(() => {
    translateProductToTemplate(product);
  }, [product]);

  useEffect(() => {
    if (securityContext.accessToken) {
      const fetchProductStates = async () => {
        const response = await fetch(
          apiUrl("/api/product/state"),
          getApiHeaders(securityContext.accessToken)
        );
        const data = await response.json();
        if (data) {
          setProductStates(data);
        }
      };

      fetchProductStates();
    }
  }, [securityContext.accessToken]);

  useEffect(() => {
    if (securityContext.accessToken) {
      const fetchOrderUnits = async () => {
        const response = await fetch(
          apiUrl("/api/product/order_unit"),
          getApiHeaders(securityContext.accessToken)
        );
        const data = await response.json();
        if (data) {
          setOrderUnits(data);
        }
      };

      fetchOrderUnits();
    }
  }, [securityContext.accessToken]);

  useEffect(() => {
    if (securityContext.accessToken) {
      const fetchWeightUnits = async () => {
        const response = await fetch(
          apiUrl("/api/product/weight_unit"),
          getApiHeaders(securityContext.accessToken)
        );
        const data = await response.json();
        if (data) {
          setWeightUnits(data);
        }
      };

      fetchWeightUnits();
    }
  }, [securityContext.accessToken]);

  useEffect(() => {
    if (securityContext.accessToken) {
      const fetchDimensionUnits = async () => {
        const response = await fetch(
          apiUrl("/api/product/dimension_unit"),
          getApiHeaders(securityContext.accessToken)
        );
        const data = await response.json();
        if (data) {
          setDimensionUnits(data);
        }
      };

      fetchDimensionUnits();
    }
  }, [securityContext.accessToken]);

  useEffect(() => {
    if (securityContext.accessToken) {
      const fetchDimensionUnits = async () => {
        const response = await fetch(
          apiUrl("/api/product/dimension_unit"),
          getApiHeaders(securityContext.accessToken)
        );
        const data = await response.json();
        if (data) {
          setDimensionUnits(data);
        }
      };

      fetchDimensionUnits();
    }
  }, [securityContext.accessToken]);

  const handleClose = () => {
    setUpdateNotification(false);
  };

  const [orderUnits, setOrderUnits] = useState<{ id: string; unit: string }[]>(
    []
  );
  const [productStates, setProductStates] = useState<
    { id: string; state: string }[]
  >([]);
  const [weightUnits, setWeightUnits] = useState<
    { id: string; unit: string }[]
  >([]);
  const [dimensionUnits, setDimensionUnits] = useState<
    { id: string; abbreviation: string; unit: string }[]
  >([]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const submit = () => {
    submitProduct(
      formToApi(
        match?.params.id && match?.params.id !== "add"
          ? match?.params.id
          : null,
        org_id!,
        formValues,
        orderUnits,
        productStates,
        weightUnits,
        dimensionUnits
      )
    );
  };

  const submitProduct = async (product: any) => {
    const fetchProducts = async () => {
      const response = await fetch(apiUrl("/api/product"), {
        ...getApiHeaders(securityContext.accessToken),
        method: "PUT",
        body: JSON.stringify(product),
      });
      const data = await response.json();
      if (data) {
        navigate(`/app/${org_id}/products/${data.id}`);
        setFormValues(apiToForm(data));
        setUpdateNotification(true);
      }
    };

    fetchProducts();
  };

  const uploadFileSuccess = (file: any) => {
    const newMedia = {
      ...formValues,
      media: [
        ...formValues.media,
        {
          display_order: formValues.media.length,
          product_id: match?.params.id !== "add" ? match?.params.id : null,
          media: file,
        },
      ],
    };
    setFormValues(newMedia);

    if (match?.params.id && match.params.id !== "add") {
      submitProduct(
        formToApi(
          match?.params.id && match.params.id !== "add"
            ? match.params.id
            : null,
          org_id!,
          newMedia,
          orderUnits,
          productStates,
          weightUnits,
          dimensionUnits
        )
      );
    }
  };

  const reorder = (media: ProductMedia[]) => {
    submitProduct(
      formToApi(
        match?.params.id && match.params.id !== "add" ? match.params.id : null,
        org_id!,
        { ...formValues, media: media },
        orderUnits,
        productStates,
        weightUnits,
        dimensionUnits
      )
    );
  };

  const remove = (media: ProductMedia) => {
    submitProduct(
      formToApi(
        match?.params.id && match.params.id !== "add" ? match.params.id : null,
        org_id!,
        {
          ...formValues,
          media: formValues.media.filter((item) => media.id !== item.id),
        },
        orderUnits,
        productStates,
        weightUnits,
        dimensionUnits
      )
    );
  };

  const deleteProduct = async () => {
    const deleteExistingProduct = async () => {
      const response = await fetch(`/api/product/${match?.params.id}`, {
        ...getApiHeaders(securityContext.accessToken),
        method: "DELETE",
      });
      const data = await response.json();
      if (data) {
        navigate(`/app/${org_id}/products`);
      }
    };
    deleteExistingProduct();
  };

  if (
    orderUnits.length === 0 ||
    productStates.length === 0 ||
    dimensionUnits.length === 0 ||
    weightUnits.length === 0
  ) {
    return null;
  }

  return (
    <>
      <Snackbar
        open={updateNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert sx={{ width: "100%" }} severity="info">
          <AlertTitle>Product Updated!</AlertTitle>
          You can now view your updated product in your store.
        </Alert>
      </Snackbar>
      <GeniePage noMargin>
        <GenieStickyHeader>
          <Box display={"flex"} alignItems={"center"}>
            <ArrowBack
              sx={{ mr: 2, cursor: "pointer" }}
              onClick={() => navigate(`/app/${org_id}/products`)}
            />
            <GenieText fontSize={"medium"} fontWeight={"bold"}>
              {match?.params.id === "add" ? "Add New" : "Edit"} Product
            </GenieText>
          </Box>
          <Box>
            <Button
              variant={"outlined"}
              size={"large"}
              onClick={submit}
              disabled={formValues.name === ""}
            >
              Save
            </Button>
          </Box>
        </GenieStickyHeader>

        <Box component={"form"} autoComplete="off" mb={4}>
          <GenieCard>
            <TextField
              id="product-name"
              name={"name"}
              label="Name"
              variant="outlined"
              fullWidth
              required
              value={formValues.name}
              onChange={handleInputChange}
            />
            <Box mt={4}>
              <TextField
                required
                name={"description"}
                id="product-description"
                fullWidth
                label="Description"
                multiline
                value={formValues.description}
                onChange={handleInputChange}
              />
            </Box>

            <Box mt={4}>
              <FormControl fullWidth>
                <InputLabel id="product-order-unit-select-label" required>
                  Wholesale Order Unit
                </InputLabel>
                <Select
                  labelId="product-order-unit-select-label"
                  name={"order_unit"}
                  id="product-order-unit-select"
                  value={formValues.order_unit}
                  required
                  label="Wholesale Order Unit"
                  onChange={handleSelectChange}
                >
                  {orderUnits.map((orderUnit) => (
                    <MenuItem key={orderUnit.id} value={orderUnit.id}>
                      {orderUnit.unit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box mt={4}>
              <TextField
                id="retail-units-per-order"
                required
                name={"retail_units"}
                value={formValues.retail_units}
                label="Number of Retail Units Per [Order Unit]"
                variant="outlined"
                placeholder={"e.g. 100"}
                fullWidth
                type={"number"}
                onChange={handleInputChange}
              />
            </Box>

            <Box mt={4}>
              <FormControl fullWidth>
                <InputLabel id="product-state-label" required>
                  Product Status
                </InputLabel>
                <Select
                  labelId="product-state-label"
                  id="product-state-select"
                  name={"state"}
                  value={formValues.state}
                  required
                  label="Product Status"
                  onChange={handleSelectChange}
                >
                  {productStates.map((productState) => (
                    <MenuItem key={productState.id} value={productState.id}>
                      {productState.state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </GenieCard>
          <GenieCard>
            <GenieText header paragraph>
              Weight
            </GenieText>
            <Grid container spacing={2}>
              <Grid item md={6}>
                <TextField
                  id="order-unit-weight"
                  type={"number"}
                  label="Weight"
                  name={"weight"}
                  value={formValues.weight}
                  onChange={handleInputChange}
                  variant="outlined"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item md={2} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="weight-unit" required>
                    Unit
                  </InputLabel>
                  <Select
                    labelId="weight-unit"
                    id="weight-unit-select"
                    name={"weight_unit"}
                    value={formValues.weight_unit}
                    onChange={handleSelectChange}
                    label="Unit"
                    required
                  >
                    {weightUnits.map((weightUnit) => (
                      <MenuItem value={weightUnit.id} key={weightUnit.id}>
                        {weightUnit.unit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </GenieCard>

          <GenieCard>
            <GenieText header paragraph>
              Pricing
            </GenieText>
            <Grid container spacing={2}>
              <Grid item md={6} sm={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel htmlFor="price" required>
                    Wholesale Price
                  </InputLabel>
                  <NumberFormat
                    decimalScale={2}
                    decimalSeparator={"."}
                    customInput={OutlinedInput}
                    id={"price"}
                    required
                    name={"price"}
                    onChange={handleInputChange}
                    label="Wholesale Price"
                    fullWidth
                    value={formValues.price}
                    startAdornment={
                      <InputAdornment position="start">$</InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item md={6} />
              <Grid item md={6} sm={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel htmlFor="msrp" required>
                    Retail Price
                  </InputLabel>
                  <NumberFormat
                    customInput={OutlinedInput}
                    decimalScale={2}
                    decimalSeparator={"."}
                    id="msrp"
                    label="Retail Price"
                    name={"msrp"}
                    fullWidth
                    required
                    onChange={handleInputChange}
                    value={formValues.msrp}
                    startAdornment={
                      <InputAdornment position="start">$</InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
          </GenieCard>

          <GenieCard>
            <GenieText header inline>
              Photos
            </GenieText>
            <GenieText subdued inline fontSize={"tiny"}>
              &nbsp;(must be square)
            </GenieText>
            <GenieCard>
              <GenieThumbList
                media={formValues.media}
                reorder={reorder}
                remove={remove}
              />
              <GenieThumbUpload uploadFile={uploadFileSuccess} />
            </GenieCard>
          </GenieCard>

          {DEBUG && (
            <GenieCard>
              <GenieText header>Debug</GenieText>
              <pre>{JSON.stringify(formValues, null, 2)}</pre>
              <Button onClick={submit}>Submit Debug</Button>
            </GenieCard>
          )}
          {match?.params.id !== "add" && (
            <GenieCard>
              <GenieText header>Remove Product</GenieText>
              <Button
                sx={{ mt: 2 }}
                color={"error"}
                variant={"contained"}
                onClick={deleteProduct}
              >
                Delete Product
              </Button>
            </GenieCard>
          )}
        </Box>
      </GeniePage>
    </>
  );
}

export default Products;
